<template>
  <section class="relative w-screen -mx-[calc(50vw-50%)] bg-zinc-50 text-zinc-900 dark:bg-black dark:text-white">
    <div class="absolute inset-0 pointer-events-none">
      <div class="absolute inset-0 transition-colors duration-500
               bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.12),transparent_60%)]
               dark:bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.18),transparent_60%)]"></div>
    </div>
    <div class="mx-auto w-[90%] px-4 md:px-6 pt-20 pb-10">
      <div class="flex flex-col md:flex-row items-start justify-between gap-6">
        <div>
          <h2 class="text-5xl sm:text-6xl font-semibold leading-[0.95] tracking-tight max-w-3xl">
            Launch faster with<br />palette resources
          </h2>
          <p class="mt-4 max-w-xl text-sm text-zinc-600 dark:text-zinc-400">
            A curated strip of NexGridCo palettes that started as posts on X and quickly turned into
            saved, bookmarked and “drop the hex codes” replies from designers.
          </p>
        </div>
        <p class="max-w-sm text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 md:text-right">
          All examples below link back to the original post on X, so you can revisit threads,
          feedback and real use cases around each palette.
        </p>
      </div>
    </div>

    <div ref="root1" class="mx-auto w-[90%] px-4 md:px-6 pb-6 select-none">
      <div class="relative overflow-hidden rounded-2xl">
        <div
          class="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-zinc-50 to-transparent dark:from-black">
        </div>
        <div
          class="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-zinc-50 to-transparent dark:from-black">
        </div>

        <div ref="track1" class="flex gap-5 will-change-transform" :style="{ transform: `translateX(${x1}px)` }"
          @mouseenter="paused1 = true" @mouseleave="paused1 = false">
          <div v-for="seg in 3" :key="'r1-seg-' + seg" class="flex gap-5 shrink-0">
            <div v-for="item in itemsRow1" :key="`r1-${seg}-${item.id}`"
              class="w-[320px] md:w-[360px] lg:w-[380px] bg-white dark:bg-zinc-900 rounded-xl ring-1 ring-black/10 dark:ring-white/10 overflow-hidden group">
              <div class="relative aspect-[16/10] overflow-hidden">
                <a :href="item.link || item.image" target="_blank" rel="noopener noreferrer"
                  class="block w-full h-full">
                  <img :src="item.image || item.cover" :alt="item.title"
                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div class="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent"></div>
                </a>
              </div>
              <div class="p-4">
                <div class="flex items-center justify-between gap-2 mb-1">
                  <div class="text-[15px] font-semibold truncate">{{ item.title }}</div>
                  <span
                    class="shrink-0 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-600 group-hover:bg-zinc-900 group-hover:text-zinc-50 dark:bg-zinc-800 dark:text-zinc-300 dark:group-hover:bg-zinc-50 dark:group-hover:text-zinc-900 transition-colors">
                    {{ item.tag }}
                  </span>
                </div>
                <p class="text-sm text-zinc-600 dark:text-zinc-300 line-clamp-2">
                  {{ item.desc }}
                </p>
                <p class="mt-2 text-[11px] text-zinc-500 dark:text-zinc-400 flex items-center gap-1">
                  <span class="inline-block h-1.5 w-1.5 rounded-full bg-sky-400"></span>
                  From a NexGridCo post on X
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div ref="root2" class="mx-auto w-[90%] px-4 md:px-6 pb-10 select-none">
      <div class="relative overflow-hidden rounded-2xl">
        <div
          class="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-zinc-50 to-transparent dark:from-black">
        </div>
        <div
          class="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-zinc-50 to-transparent dark:from-black">
        </div>

        <div ref="track2" class="flex gap-5 will-change-transform" :style="{ transform: `translateX(${x2}px)` }"
          @mouseenter="paused2 = true" @mouseleave="paused2 = false">
          <div v-for="seg in 3" :key="'r2-seg-' + seg" class="flex gap-5 shrink-0">
            <div v-for="item in itemsRow2" :key="`r2-${seg}-${item.id}`"
              class="w-[320px] md:w-[360px] lg:w-[380px] bg-white dark:bg-zinc-900 rounded-xl ring-1 ring-black/10 dark:ring-white/10 overflow-hidden group">
              <div class="relative aspect-[16/10] overflow-hidden">
                <a :href="item.link || item.image" target="_blank" rel="noopener noreferrer"
                  class="block w-full h-full">
                  <img :src="item.image || item.cover" :alt="item.title"
                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div class="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent"></div>
                </a>
              </div>
              <div class="p-4">
                <div class="flex items-center justify-between gap-2 mb-1">
                  <div class="text-[15px] font-semibold truncate">{{ item.title }}</div>
                  <span
                    class="shrink-0 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-600 group-hover:bg-zinc-900 group-hover:text-zinc-50 dark:bg-zinc-800 dark:text-zinc-300 dark:group-hover:bg-zinc-50 dark:group-hover:text-zinc-900 transition-colors">
                    {{ item.tag }}
                  </span>
                </div>
                <p class="text-sm text-zinc-600 dark:text-zinc-300 line-clamp-2">
                  {{ item.desc }}
                </p>
                <p class="mt-2 text-[11px] text-zinc-500 dark:text-zinc-400 flex items-center gap-1">
                  <span class="inline-block h-1.5 w-1.5 rounded-full bg-sky-400"></span>
                  From a NexGridCo post on X
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 flex justify-center">
        <div class="h-1 w-1 rounded-full bg-zinc-400/70 dark:bg-white/60"></div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

const makeCover = (hex, label, accent) => {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900'>
      <defs>
        <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
          <stop offset='0' stop-color='${hex}' stop-opacity='0.98'/>
          <stop offset='1' stop-color='#020617' stop-opacity='1'/>
        </linearGradient>
        <radialGradient id='r1' cx='18%' cy='20%' r='45%'>
          <stop offset='0' stop-color='${accent}' stop-opacity='0.5'/>
          <stop offset='1' stop-color='${accent}' stop-opacity='0'/>
        </radialGradient>
        <radialGradient id='r2' cx='82%' cy='78%' r='55%'>
          <stop offset='0' stop-color='#ffffff' stop-opacity='0.22'/>
          <stop offset='1' stop-color='#ffffff' stop-opacity='0'/>
        </radialGradient>
      </defs>
      <rect width='1600' height='900' fill='url(#g)'/>
      <circle cx='320' cy='200' r='420' fill='url(#r1)'/>
      <circle cx='1280' cy='640' r='520' fill='url(#r2)'/>
      <g font-family='system-ui,-apple-system,BlinkMacSystemFont,"SF Pro Text",sans-serif' font-weight='800' font-size='150' fill='#ffffff' opacity='0.9'>
        <text x='90' y='520'>${label}</text>
      </g>
    </svg>`
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

const base = [
  {
    id: 1,
    cover: makeCover('#0EA5E9', 'Palette Kit', '#22C55E'),
    image: 'https://pbs.twimg.com/media/G6I1M-2XIAIB_gl?format=jpg&name=900x900',
    link: 'https://x.com/nexgridco/status/1991212486494675406',
    title: 'Base Palette Kit',
    tag: 'Template',
    desc: 'Start from a balanced primary + neutral palette tuned for product UIs.'
  },
  {
    id: 2,
    cover: makeCover('#4F46E5', 'Contrast Lab', '#FACC15'),
    image: 'https://pbs.twimg.com/media/G5RsdtpWkAA9r1W?format=jpg&name=900x900',
    link: 'https://x.com/nexgridco/status/1987332596951355748',
    title: 'Contrast Lab (AA/AAA)',
    tag: 'Tool',
    desc: 'WCAG-ready pairs for headings, body text and interactive states.'
  },
  {
    id: 3,
    cover: makeCover('#EC4899', 'Marketing', '#F97316'),
    image: 'https://pbs.twimg.com/media/G5PDz-7XAAAZVip?format=jpg&name=medium',
    link: 'https://x.com/nexgridco/status/1987147162392686696',
    title: 'Marketing Gradient Deck',
    tag: 'Pack',
    desc: 'Hero gradients and accent palettes for launch pages and campaigns.'
  },
  {
    id: 4,
    cover: makeCover('#22C55E', 'Tokens', '#0EA5E9'),
    image: 'https://pbs.twimg.com/media/G5zEdDiW0AA0r9k?format=jpg&name=900x900',
    link: 'https://x.com/nexgridco/status/1989681142975410535',
    title: 'Semantic Token System',
    tag: 'Template',
    desc: 'Map role-based tokens to real colors: success, info, warning and error.'
  },
  {
    id: 5,
    cover: makeCover('#020617', 'Dark UI', '#38BDF8'),
    image: 'https://pbs.twimg.com/media/G6N6xlHW0AAsHg8?format=jpg&name=900x900',
    link: 'https://x.com/nexgridco/status/1991570456474972280',
    title: 'Dark Mode Surface System',
    tag: 'System',
    desc: 'Layered surfaces, overlays and borders designed for dark-first products.'
  },
  {
    id: 6,
    cover: makeCover('#F97316', 'Brand', '#EC4899'),
    image: 'https://pbs.twimg.com/media/G6KtLNWXEAAR0NQ?format=jpg&name=900x900',
    link: 'https://x.com/nexgridco/status/1991344397993275704',
    title: 'Brand & Product Palettes',
    tag: 'Kit',
    desc: 'Align brand colors with product-safe variants and neutral rails.'
  },
  {
    id: 7,
    cover: makeCover('#64748B', 'Neutral', '#0EA5E9'),
    image: 'https://pbs.twimg.com/media/G6IDL_BWQAEsnrT?format=jpg&name=900x900',
    link: 'https://x.com/nexgridco/status/1991157495016943646',
    title: 'Neutral Framework',
    tag: 'System',
    desc: 'Greyscale ramps that keep typography, icons and borders consistent.'
  },
  {
    id: 8,
    cover: makeCover('#EF4444', 'Signals', '#22C55E'),
    image: 'https://pbs.twimg.com/media/G6KhIWtXMAAKDOe?format=jpg&name=900x900',
    link: 'https://x.com/nexgridco/status/1991331155946217935',
    title: 'Accent & Signal Pack',
    tag: 'Pack',
    desc: 'Carefully spaced accent and status colors for high-clarity feedback.'
  }
]

const itemsRow1 = [base[0], base[2], base[3], base[5], base[1], base[4], base[6], base[7]]
const itemsRow2 = [base[4], base[7], base[1], base[6], base[0], base[3], base[2], base[5]]

const root1 = ref(null)
const root2 = ref(null)
const track1 = ref(null)
const track2 = ref(null)

const x1 = ref(0)
const x2 = ref(0)

const speed1 = ref(0.22)
const speed2 = ref(0.22)

const paused1 = ref(false)
const paused2 = ref(false)

let segW1 = 0
let segW2 = 0
let raf1 = null
let raf2 = null
let obs1 = null
let obs2 = null
let vis1 = false
let vis2 = false

function measureWidth(trackEl) {
  if (!trackEl) return 0
  const children = Array.from(trackEl.children || [])
  if (children.length < 2) return 0
  const a = children[0].getBoundingClientRect().width || 0
  const b = children[1].getBoundingClientRect().width || 0
  return a + b
}

function loopRow1() {
  if (!paused1.value && vis1 && segW1 > 0) {
    x1.value += speed1.value
    if (x1.value >= 0) x1.value -= segW1
  }
  raf1 = requestAnimationFrame(loopRow1)
}

function loopRow2() {
  if (!paused2.value && vis2 && segW2 > 0) {
    x2.value -= speed2.value
    if (x2.value <= -segW2) x2.value += segW2
  }
  raf2 = requestAnimationFrame(loopRow2)
}

function normalizeRow1() {
  if (!segW1) return
  x1.value = -(((-x1.value) % segW1))
  if (x1.value === 0) x1.value = -segW1
}

function normalizeRow2() {
  if (!segW2) return
  x2.value = -(((-x2.value) % segW2))
}

function onResize() {
  segW1 = measureWidth(track1.value)
  segW2 = measureWidth(track2.value)
  normalizeRow1()
  normalizeRow2()
}

onMounted(() => {
  segW1 = measureWidth(track1.value)
  segW2 = measureWidth(track2.value)
  x1.value = -segW1
  x2.value = 0

  obs1 = new IntersectionObserver(([e]) => { vis1 = !!e?.isIntersecting }, { threshold: 0.1 })
  obs2 = new IntersectionObserver(([e]) => { vis2 = !!e?.isIntersecting }, { threshold: 0.1 })

  if (root1.value) obs1.observe(root1.value)
  if (root2.value) obs2.observe(root2.value)

  raf1 = requestAnimationFrame(loopRow1)
  raf2 = requestAnimationFrame(loopRow2)

  window.addEventListener('resize', onResize, { passive: true })
})

onBeforeUnmount(() => {
  if (raf1) cancelAnimationFrame(raf1)
  if (raf2) cancelAnimationFrame(raf2)
  if (obs1 && root1.value) obs1.unobserve(root1.value)
  if (obs2 && root2.value) obs2.unobserve(root2.value)
  window.removeEventListener('resize', onResize)
})
</script>

<style></style>
